import { useState, type ReactElement } from 'react';
import { Button } from 'react-aria-components';
import { Icon } from '../ui/Icon';
import { useHelpAnchorPresent } from './showMe';
import type { HelpAnchorRef } from './types';

/**
 * Help's "Show me" button (APP-090), under a chat answer or a help topic. It is there only while
 * the thing is on the page and visible; pressing it scrolls there, highlights it and moves focus
 * to it (the shell announces it). After that, the words say where it is.
 */
export function ShowMeButton({
  anchor,
  showMe,
  className,
  noteClassName,
}: {
  anchor: HelpAnchorRef;
  showMe: (anchor: HelpAnchorRef) => boolean;
  className?: string;
  noteClassName?: string;
}): ReactElement | null {
  const present = useHelpAnchorPresent(anchor.id);
  const [shown, setShown] = useState(false);
  if (!present) return null;
  return (
    <>
      {/* The name says what it shows; browsers join hidden text differently, so it's spelled out. */}
      <Button
        {...(className !== undefined && { className })}
        aria-label={`Show me ${anchor.label}`}
        onPress={() => {
          setShown(showMe(anchor));
        }}
      >
        <Icon name="target" size={16} />
        Show me
      </Button>
      {shown && <span className={noteClassName}>Highlighted on the page: {anchor.label}.</span>}
    </>
  );
}
